import {
  Box,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import { createFileRoute } from "@tanstack/react-router";
import { IOTDeviceWidgetGrid } from "@dmca/frontend/widgets/iot";
import { useQuery } from "@tanstack/react-query";
import {
  IIOTDeviceWithStatus,
  IOTDeviceHardwareTypeEnum,
  IOTDeviceHardwareTypeEnumNameMap,
} from "@dmca/models/interface/iot-device";
import {
  checkPermissionsInLoader,
  useSubscription,
} from "@dmca/frontend/hooks";
import { PageHeader } from "@dmca/frontend/components/nav";
import { Select } from "@dmca/frontend/components/input";
import { useState } from "react";

const ALL_PLANTS = "__all__";
type DeviceSort = "defaultName" | "plantName" | "bayNumber" | "columnNumber";

export const Route = createFileRoute("/_authed/iot")({
  component: HeartbeatPage,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("IOT::ViewIOTOverview", context);
    const { trpcQcProxy: trpc, qc } = context;
    const devices = await qc.ensureQueryData(
      trpc.iot.getAllWithStatus.queryOptions()
    );

    return { devices };
  },
  head: () => ({
    meta: [
      {
        title: "IoT Device Overview | DNA Dashboard",
      },
    ],
  }),
});

const visibleDeviceTypes = [
  IOTDeviceHardwareTypeEnum.RPI,
  IOTDeviceHardwareTypeEnum.ZEBRA_FX9600,
];

function sortDevicesByCategory(
  data: IIOTDeviceWithStatus[],
  sortBy: DeviceSort,
  descending: boolean
): IIOTDeviceWithStatus[][] {
  return Object.entries(IOTDeviceHardwareTypeEnumNameMap)
    .filter(([enumKey]) =>
      visibleDeviceTypes.includes(
        enumKey as keyof typeof IOTDeviceHardwareTypeEnumNameMap
      )
    )
    .map(([enumKey]) => {
      return data
        .filter((device) => device.hardwareType === enumKey)
        .sort((a, b) => {
          const direction = descending ? -1 : 1;
          const aVal =
            sortBy === "defaultName" ? a.defaultName : a.cellLocation[sortBy];
          const bVal =
            sortBy === "defaultName" ? b.defaultName : b.cellLocation[sortBy];
          if (aVal == null || bVal == null) {
            if (aVal != null) return -1;
            if (bVal != null) return 1;
          } else if (typeof aVal === "number" && typeof bVal === "number") {
            if (aVal !== bVal) return (aVal - bVal) * direction;
          } else if (typeof aVal === "string" && typeof bVal === "string") {
            const result = aVal.localeCompare(bVal, undefined, {
              numeric: true,
              sensitivity: "base",
            });
            if (result) return result * direction;
          }
          const result =
            a.defaultName.localeCompare(b.defaultName) ||
            a.id.localeCompare(b.id);
          return result * direction;
        });
    });
}

function HeartbeatPage() {
  const [selectedPlantId, setSelectedPlantId] = useState(ALL_PLANTS);
  const [sortBy, setSortBy] = useState<DeviceSort>("defaultName");
  const [descending, setDescending] = useState(false);
  const { devices } = Route.useLoaderData();
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const { data } = useQuery(
    trpc.iot.getAllWithStatus.queryOptions(undefined, {
      initialData: devices,
    })
  );
  const visibleEntries = Object.entries(
    IOTDeviceHardwareTypeEnumNameMap
  ).filter(([enumKey]) =>
    visibleDeviceTypes.includes(
      enumKey as keyof typeof IOTDeviceHardwareTypeEnumNameMap
    )
  );

  const plantOptions = [
    { name: "All Plants", value: ALL_PLANTS },
    ...Array.from(
      new Map(
        data.map((device) => [
          device.cellLocation.plantId,
          {
            name: device.cellLocation.plantName,
            value: device.cellLocation.plantId,
          },
        ])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name)),
  ];
  const visibleDevices = data.filter(
    (device) =>
      visibleDeviceTypes.includes(
        device.hardwareType as keyof typeof IOTDeviceHardwareTypeEnumNameMap
      ) &&
      (selectedPlantId === ALL_PLANTS ||
        device.cellLocation.plantId === selectedPlantId)
  );
  const offlineCount = visibleDevices.filter(
    (device) => !device.inMaintenance && device.status === "offline"
  ).length;
  const onlineCount = visibleDevices.filter(
    (device) => !device.inMaintenance && device.status === "online"
  ).length;

  const maintenanceCount = visibleDevices.filter(
    (device) => device.inMaintenance
  ).length;
  const sortedDevices = sortDevicesByCategory(
    visibleDevices,
    sortBy,
    descending
  );

  useSubscription("onIOTDeviceStatusUpdate", () => {
    qc.invalidateQueries(trpc.iot.pathFilter());
  });

  return (
    <>
      <PageHeader>IoT Device Overview</PageHeader>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={{ mb: 2 }}>
        <Select
          label="Plant"
          allowNull={false}
          value={selectedPlantId}
          options={plantOptions}
          onChange={(event) => setSelectedPlantId(event.target.value)}
          sx={{ maxWidth: 240 }}
        />
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Select
            label="Sort by"
            allowNull={false}
            value={sortBy}
            options={[
              { name: "Cell name", value: "defaultName" },
              { name: "Plant", value: "plantName" },
              { name: "Bay #", value: "bayNumber" },
              { name: "Column #", value: "columnNumber" },
            ]}
            onChange={(event) => setSortBy(event.target.value as DeviceSort)}
            sx={{ width: 240 }}
          />
          <Tooltip title={`Sort ${descending ? "ascending" : "descending"}`}>
            <IconButton
              aria-label={`Sort ${descending ? "ascending" : "descending"}`}
              onClick={() => setDescending((value) => !value)}
            >
              {descending ? <ArrowDownward /> : <ArrowUpward />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {offlineCount > 0 && (
          <Chip
            data-testid="iot-offline-count"
            color="error"
            label={`${offlineCount} Devices Offline`}
          />
        )}
        {onlineCount > 0 && (
          <Chip
            data-testid="iot-online-count"
            color="success"
            label={`${onlineCount} Devices Online`}
          />
        )}
        {maintenanceCount > 0 && (
          <Chip
            data-testid="iot-maintenance-count"
            color="warning"
            label={`${maintenanceCount} Devices in Maintenance`}
          />
        )}
      </Box>

      <Stack divider={<Divider />}>
        {data ? (
          visibleEntries.map(([enumKey, gridTitle], index) => {
            return (
              <IOTDeviceWidgetGrid
                key={enumKey}
                gridTitle={gridTitle}
                data={sortedDevices[index]}
              />
            );
          })
        ) : (
          <Skeleton variant="rectangular" sx={{ flexGrow: 1 }} />
        )}
      </Stack>
    </>
  );
}
